export type LeaderboardEntry = { playerId: string; playerName: string; score: number };

/**
 * Sync a local player list from an authoritative backend leaderboard.
 *
 * The server builds leaderboards from getStandingsPlayers() (intentionally-left
 * players excluded). The leaderboard is the roster + scores; we preserve avatars
 * from the current list and do not keep local-only players that left.
 *
 * Server scores win — never Math.max with FE state. Keeping a higher FE score
 * caused Play Again / new-game totals to stick, and hid real bonus credits when
 * the client was still showing a previous game's standing.
 */
export function syncPlayersFromLeaderboard<
  T extends { id: string; name: string; score: number; avatar?: string },
>(currentPlayers: T[], leaderboard: LeaderboardEntry[] | undefined): T[] {
  if (!Array.isArray(leaderboard) || leaderboard.length === 0) {
    return currentPlayers;
  }
  const currentById = new Map(currentPlayers.map((p) => [p.id, p]));
  return leaderboard.map((entry) => {
    const existing = currentById.get(entry.playerId);
    return {
      ...existing,
      id: entry.playerId,
      name: entry.playerName,
      score: entry.score,
      avatar: existing?.avatar,
    } as T;
  });
}

/**
 * Merge incoming server players without lowering scores (stale room-state after bonus / live scoring).
 *
 * Exception: when the server resets everyone to 0 (Play Again / new game), trust the
 * server — otherwise FE keeps the previous game's totals forever.
 */
export function mergePlayersWithoutScoreRegression<
  T extends { id: string; score: number },
>(incoming: T[], previous: T[] | undefined, options?: { allowRegression?: boolean }): T[] {
  if (!previous?.length || options?.allowRegression) return incoming;

  const serverResetToZero =
    incoming.length > 0 &&
    incoming.every((p) => (p.score ?? 0) === 0) &&
    previous.some((p) => (p.score ?? 0) > 0);
  if (serverResetToZero) return incoming;

  const prevById = new Map(previous.map((p) => [p.id, p]));
  return incoming.map((p) => {
    const prev = prevById.get(p.id);
    if (prev && prev.score > p.score) {
      return { ...p, score: prev.score };
    }
    return p;
  });
}

/**
 * Apply an authoritative per-player total from bonus-answer-ack so the score
 * strip matches BE the moment points are awarded (before the next room-state).
 */
export function applyAuthoritativePlayerTotal<
  T extends { id: string; score: number },
>(players: T[] | undefined, playerId: string | undefined, totalScore: number): T[] | undefined {
  if (!players?.length || !playerId || !Number.isFinite(totalScore) || totalScore < 0) {
    return players;
  }
  return players.map((p) => (p.id === playerId ? { ...p, score: totalScore } : p));
}
